import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    BarChartSummary,
    BarChartComparison,
    VerticalPositiveNegativeBarChart,
    VerticalBarChartComparison,
    ComparativeTable,
    StackedBarChartComparison,
    LineChartComparison,
    CustomPieChart
} from "../Charts";
import { selectDates, selectTopN, selectShowFeminicidios, selectComparisonYear } from "../../redux/slices/filtersSlice";
import {
    filterData,
    processFilteredData,
    groupAgeByRange,
    applyFilterLogic,
    groupByFieldWithCategories
} from "../../utils/filterFunctions";
import "./Pages.css";
import { useFetchWithSpinner } from "../../hooks/useFetchWithSpinner";

const Homicidios = () => {
    const [data, setData] = useState([]);
    const [dataFem, setDataFem] = useState([]);

    const dates = useSelector(selectDates);
    const comparisonYear = useSelector(selectComparisonYear);
    const topN = useSelector(selectTopN);
    const showFeminicidios = useSelector(selectShowFeminicidios);
    const baseFilters = {
        sexo: null,
        caracterizacion: null,
        comuna: null,
        modalidad: null,
        rangoEdad: null,
        diaJornada: null,
        mes: null,
        categoria: null,
        tipo_arma: null,
        tipo_feminicidio: null,
        feminicidio: null,
    };

    const [filters, setFilters] = useState({ ...baseFilters });
    const [frozenKey, setFrozenKey] = useState(null);
    const [filtersFem, setFiltersFem] = useState({ ...baseFilters });
    const [frozenKeyFem, setFrozenKeyFem] = useState(null);
    const fetchWithSpinner = useFetchWithSpinner();

    useEffect(() => {
        if (dates.start && dates.end) {
            fetchWithSpinner({
                startDate: dates.start,
                endDate: dates.end,
                year_to_compare: comparisonYear,
                endpoint: 'homicidios/',
            }, setData);

            if (showFeminicidios) {
                fetchWithSpinner({
                    startDate: dates.start,
                    endDate: dates.end,
                    year_to_compare: comparisonYear,
                    endpoint: 'homicidios/feminicidios',
                }, setDataFem);
            } else {
                setDataFem([]);
            }
        }
    }, [dates.start, dates.end, showFeminicidios, comparisonYear]);

    const currentYear = dates.end ? new Date(dates.end).getFullYear() : new Date().getFullYear();

    const ranges = [
        { label: "0-5", min: 0, max: 5 }, { label: "6-11", min: 6, max: 11 },
        { label: "12-13", min: 12, max: 13 }, { label: "14-17", min: 14, max: 17 },
        { label: "18-24", min: 18, max: 24 }, { label: "25-28", min: 25, max: 28 },
        { label: "29-32", min: 29, max: 32 }, { label: "33-38", min: 33, max: 38 },
        { label: "39-45", min: 39, max: 45 }, { label: "46-52", min: 46, max: 52 },
        { label: "53-59", min: 53, max: 59 }, { label: "60-66", min: 60, max: 66 },
        { label: "Mayor-67", min: 67, max: Infinity }, { label: "Sin Dato", min: null, max: null },
    ];

    const homicidiosData = useMemo(() => {
        const filteredData = filterData(data, filters);

        const dias = [...new Set(data.map(h => h.dia))];
        const jornadas = [...new Set(data.map(h => h.jornada))];

        const results = processFilteredData(filteredData, {
            includeTotals: true,
            groupByFields: ["mes", "caracterizacion", "sexo", "comuna", "modalidad"],
            includeDayShift: true,
            dias,
            jornadas
        }, comparisonYear, currentYear);

        const { totals: { diferencia, variacion }, resumenData } = results;

        const mesData = results.groups.mes;

        const caracterizacionDataRaw = results.groups.caracterizacion.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const caracterizacionData = caracterizacionDataRaw.slice(0, Math.min(topN, caracterizacionDataRaw.length));

        const sexoData = results.groups.sexo.filter(item => item.name === "Hombre" || item.name === "Mujer").sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const comunaData = results.groups.comuna.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

        const modalidadDataRaw = results.groups.modalidad.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const modalidadData = modalidadDataRaw.slice(0, Math.min(topN, modalidadDataRaw.length));

        const jovenesData = [
            {
                name: "Año",
                [comparisonYear]: filteredData.filter(h => h.año === comparisonYear && h.edad >= 14 && h.edad <= 25).length,
                [currentYear]: filteredData.filter(h => h.año === currentYear && h.edad >= 14 && h.edad <= 25).length
            },
        ];


        const diasJornadaData1 = results.diasJornadaData[comparisonYear];
        const diasJornadaData2 = results.diasJornadaData[currentYear];

        const edadData = groupAgeByRange(filteredData, ranges, comparisonYear, currentYear).sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

        return {
            resumenData, diferencia, variacion, jovenesData, sexoData, caracterizacionData,
            mesData, edadData, diasJornadaData1, diasJornadaData2, comunaData, modalidadData
        };
    }, [data, filters, topN, currentYear, comparisonYear]);

    const feminicidiosData = useMemo(() => {
        if (dataFem.length === 0) return {};

        const filteredDataFem = filterData(dataFem, filtersFem);

        const resultsFem = processFilteredData(filteredDataFem, {
            includeTotals: true,
            groupByFields: ["categoria", "tipo_arma", "tipo_feminicidio", "feminicidio", "comuna"]
        }, comparisonYear, currentYear);

        const femTotal = resultsFem.totals;
        const resumenDataFem = resultsFem.resumenData;

        const categoriaDataFem = resultsFem.groups.categoria.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

        const tipoArmaDataRawFem = resultsFem.groups.tipo_arma.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const tipoArmaDataFem = tipoArmaDataRawFem.slice(0, Math.min(topN, tipoArmaDataRawFem.length));

        const tipoFeminicidioDataRawFem = resultsFem.groups.tipo_feminicidio.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const tipoFeminicidioDataFem = tipoFeminicidioDataRawFem.slice(0, Math.min(topN, tipoFeminicidioDataRawFem.length));

        const feminicidioDataFem = resultsFem.groups.feminicidio.map(item => ({
            name: item.name,
            total: Object.entries(item).filter(([k]) => k !== 'name').reduce((acc, [, v]) => acc + v, 0)
        })).sort((a, b) => b.total - a.total);

        const feminicidioByMonthFem = groupByFieldWithCategories(
            filteredDataFem,
            "mes",
            "feminicidio",
            ["si", "no"],
            (val) => {
                const normalized = String(val || "").toLowerCase().trim();
                return normalized === "si" || normalized === "sí" ? "si" : "no";
            }
        );

        const comunaDataFem = resultsFem.groups.comuna.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
        const edadDataFem = groupAgeByRange(filteredDataFem, ranges, comparisonYear, currentYear).sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

        return {
            femTotal, resumenDataFem, categoriaDataFem, tipoArmaDataFem, tipoFeminicidioDataFem,
            feminicidioDataFem, feminicidioByMonthFem, comunaDataFem, edadDataFem
        };
    }, [dataFem, filtersFem, topN, currentYear, comparisonYear]);

    const handleFilterClick = (tipo, valor, source, nameFilter = "Homicidios") => {
        const isFeminicidioFilter = nameFilter === "Feminicidios";
        const targetFilters = isFeminicidioFilter ? filtersFem : filters;
        const setTargetFilters = isFeminicidioFilter ? setFiltersFem : setFilters;
        const targetFrozenKey = isFeminicidioFilter ? frozenKeyFem : frozenKey;
        const setTargetFrozenKey = isFeminicidioFilter ? setFrozenKeyFem : setFrozenKey;
        const targetBaseFilters = isFeminicidioFilter ? baseFilters : baseFilters;

        setTargetFilters((prevFilters) =>
            applyFilterLogic(prevFilters, tipo, valor, source, targetFrozenKey, setTargetFrozenKey, targetBaseFilters)
        );
    };

    const {
        resumenData, diferencia, variacion, jovenesData, sexoData, caracterizacionData, mesData,
        edadData, diasJornadaData1, diasJornadaData2, comunaData, modalidadData
    } = homicidiosData;

    const {
        femTotal, resumenDataFem, categoriaDataFem, tipoArmaDataFem, tipoFeminicidioDataFem,
        feminicidioDataFem, feminicidioByMonthFem, comunaDataFem, edadDataFem
    } = feminicidiosData;

    return (
        <div className="dashboard-container">
            {/* VISTA DE HOMICIDIOS */}
            {!showFeminicidios && (
                <div className="dashboard-grid">
                    <div className="row top">
                        <div className="card small">
                            <BarChartSummary
                                resumenData={resumenData}
                                diferencia={diferencia}
                                variacion={variacion}
                                xKey="name"
                                title="Resumen general"
                                selectedKey={filters.año}
                                frozenKey={frozenKey}
                                onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general")}
                            />
                        </div>
                        <div className="card small">
                            <BarChartComparison
                                showData={jovenesData}
                                xKey="name"
                                title="Jóvenes entre 14 y 25 años"
                                showTick={false}
                            />
                        </div>
                        <div className="card small">
                            <VerticalPositiveNegativeBarChart
                                showData={sexoData}
                                xKey="name"
                                title="Sexo"
                                selectedKey={filters.sexo}
                                frozenKey={frozenKey}
                                onBarClick={(d) => handleFilterClick("sexo", d?.category, "Sexo")}
                                comparisonYear={comparisonYear}
                            />
                        </div>
                        <div className="card wide">
                            <BarChartComparison
                                showData={caracterizacionData}
                                xKey="name"
                                title="Caracterización"
                                selectedKey={filters.caracterizacion}
                                frozenKey={frozenKey}
                                onBarClick={(d) => handleFilterClick("caracterizacion", d?.category, "Caracterización")}
                            />
                        </div>
                    </div>
                    <div className="row middle">
                        <div className="column-right">
                            <div className="row bottom">
                                <div className="card medium">
                                    <VerticalPositiveNegativeBarChart
                                        showData={edadData}
                                        xKey="rango"
                                        title="Rango de edad"
                                        selectedKey={filters.rangoEdad}
                                        frozenKey={frozenKey}
                                        onBarClick={(d) => handleFilterClick("rangoEdad", d?.category, "Rango de edad")}
                                        comparisonYear={comparisonYear}
                                    />
                                </div>
                                <div className="card medium">
                                    <BarChartComparison
                                        showData={modalidadData}
                                        xKey="name"
                                        title="Modalidad"
                                        selectedKey={filters.modalidad}
                                        frozenKey={frozenKey}
                                        onBarClick={(d) => handleFilterClick("modalidad", d?.category, "Modalidad")}
                                    />
                                </div>
                                <div className="card wide">
                                    <LineChartComparison
                                        showData={mesData}
                                        xKey="name"
                                        title="Homicidios por mes"
                                        selectedKey={filters.mes}
                                        frozenKey={frozenKey}
                                        onBarClick={(d) => handleFilterClick("mes", d?.category, "Homicidios por mes")}
                                    />
                                </div>
                            </div>
                            <div className="row bottom">
                                <div className="card medium">
                                    <BarChartComparison
                                        showData={comunaData}
                                        xKey="name"
                                        title="Homicidios por comuna"
                                        selectedKey={filters.comuna}
                                        frozenKey={frozenKey}
                                        onBarClick={(d) => handleFilterClick("comuna", d?.category, "Homicidios por comuna")}
                                    />
                                </div>
                                <div className="card medium">
                                    <ComparativeTable
                                        previousData={diasJornadaData1}
                                        previousYear={comparisonYear}
                                        currentData={diasJornadaData2}
                                        currentYear={currentYear}
                                        title="Homicidios por día y jornada"
                                        selectedKey={filters.diaJornada}
                                        frozenKey={frozenKey}
                                        onBarClick={(d) => handleFilterClick("diaJornada", d?.category, "Homicidios por día y jornada")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* VISTA DE FEMINICIDIOS */}
            {showFeminicidios && (
                <div className="dashboard-grid">
                    <div className="row top">
                        <div className="card small">
                            <BarChartSummary
                                resumenData={resumenDataFem}
                                diferencia={femTotal?.diferencia}
                                variacion={femTotal?.variacion}
                                xKey="name"
                                title="Resumen general"
                                selectedKey={filtersFem.año}
                                frozenKey={frozenKeyFem}
                                onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general", "Feminicidios")}
                            />
                        </div>
                        <div className="card small">
                            <BarChartComparison
                                showData={categoriaDataFem}
                                xKey="name"
                                title="Categoría"
                                selectedKey={filtersFem.categoria}
                                frozenKey={frozenKeyFem}
                                onBarClick={(d) => handleFilterClick("categoria", d?.category, "Categoría", "Feminicidios")}
                            />
                        </div>
                        <div className="card small">
                            <BarChartComparison
                                showData={tipoArmaDataFem}
                                xKey="name"
                                title="Tipo de arma"
                                selectedKey={filtersFem.tipo_arma}
                                frozenKey={frozenKeyFem}
                                onBarClick={(d) => handleFilterClick("tipo_arma", d?.category, "Tipo de arma", "Feminicidios")} />
                        </div>
                        <div className="card small">
                            <BarChartComparison
                                showData={tipoFeminicidioDataFem}
                                xKey="name"
                                title="Tipo de feminicidio"
                                selectedKey={filtersFem.tipo_feminicidio}
                                frozenKey={frozenKeyFem}
                                onBarClick={(d) => handleFilterClick("tipo_feminicidio", d?.category, "Tipo de feminicidio", "Feminicidios")}
                            />
                        </div>
                    </div>
                    <div className="row middle">
                        <div className="column-right">
                            <div className="row bottom">
                                <div className="card medium">
                                    <VerticalPositiveNegativeBarChart
                                        showData={edadDataFem}
                                        xKey="rango"
                                        title="Rango de edad"
                                        selectedKey={filtersFem.rangoEdad}
                                        frozenKey={frozenKeyFem}
                                        onBarClick={(d) => handleFilterClick("rangoEdad", d?.category, "Rango de edad", "Feminicidios")}
                                        comparisonYear={comparisonYear}
                                    />
                                </div>
                                <div className="card medium">
                                    <CustomPieChart
                                        showData={feminicidioDataFem}
                                        xKey="name"
                                        title="Feminicidios (Sí/No)"
                                        selectedKey={filtersFem.feminicidio}
                                        frozenKey={frozenKeyFem}
                                        onBarClick={(d) => handleFilterClick("feminicidio", d?.category, "Feminicidios (Sí/No)", "Feminicidios")}
                                    />
                                </div>
                            </div>
                            <div className="row bottom">
                                <div className="card medium">
                                    <BarChartComparison
                                        showData={comunaDataFem}
                                        xKey="name"
                                        title="Feminicidios por comuna"
                                        selectedKey={filtersFem.comuna}
                                        frozenKey={frozenKeyFem}
                                        onBarClick={(d) => handleFilterClick("comuna", d?.category, "Feminicidios por comuna", "Feminicidios")}
                                    />
                                </div>
                                <div className="card medium">
                                    <StackedBarChartComparison
                                        showData={feminicidioByMonthFem}
                                        xKey="name"
                                        title="Homicidios de mujeres por mes (incluye feminicidio)"
                                        selectedKey={filtersFem.mes}
                                        frozenKey={frozenKeyFem}
                                        onBarClick={(d) => handleFilterClick("mes", d?.category, "Homicidios de mujeres por mes (incluye feminicidio)", "Feminicidios")}
                                        colors={['#00BFFF', '#682677ff']}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>)}
        </div>
    );
};

export default Homicidios;